
import { defineComponent } from "vue";
import { storeToRefs } from "pinia";
import { useActiveStore } from "@/stores/ssaStore/active";
import { usePvwStore } from "@/stores/ssaStore/pvw";
import { DatasetType, IParaviewColumnMapping, ParaviewFilterPlus } from "@/types/dataset";
import { BlockSortOrder } from "@/utils/DisplayPaneConstants";
import { LayerNameToColumnLayerIdentifier } from "@/utils/DisplayPane";
import { ParaviewFilter } from "@/types/dataset";
import { useDisplayPaneStore } from "@/stores/ssaStore/displayPane";
import { useFiltersStore } from "@/stores/ssaStore/filters";
import moment from "moment-timezone";

interface SelectInputItems {
	text: string;
	value: string;
	isTimeStamp?: boolean;
}
interface LayerConfig {
	layerConfigType: string;
	displayPaneLabel: string;
	defaultProperty: string;
	pickProperty: string;
	fileExtension: string;
	hierarchies: string[];
}

export default defineComponent({
	name: "threshold-filter-options",
	components: {},
	setup() {
		const {
			SSA_ACTIVE_DATASET: activeDataset,
			dataset: datasets,
			view: activeView
		} = storeToRefs(useActiveStore());
		// const displayPaneStore = useDisplayPaneStore();
		const { displayStates } = storeToRefs(useDisplayPaneStore());
		const pvwStore = usePvwStore();
		const activeStore = useActiveStore();
		const filtersStore = useFiltersStore();
		return {
			datasets,
			activeView,
			pvwStore,
			activeStore,
			displayStates,
			activeDataset,
			filtersStore
		};
	},
	props: {
		isLoading: {
			type: Boolean,
			default: false
		},
		activateLayer: {
			type: Function,
			required: true
		},
		initialLayerValue: {
			type: String,
			default: ""
		},
		initialPropValue: {
			type: String,
			default: ""
		}
	},
	mounted() {
		if (this.layerItems.length === 1) {
			this.selectedLayerValue = this.layerItems[0].value;
		}
		const val = this.existingActiveFilterInclHidden;
		if (
			val &&
			(val.disableButDisplayFilter || val.disableButDisplayFilterSlashEye) &&
			val.hiddenRangeValues
		) {
			// if the filter is hidden, set fake values for the user to adjust

			this.startDateFake = moment.unix(val.hiddenRangeValues[0]).format("YYYY-MM-DD");
			this.endDateFake = moment.unix(val.hiddenRangeValues[1]).format("YYYY-MM-DD");

			this.activeMinFake = val.hiddenRangeValues[0];
			this.activeMaxFake = val.hiddenRangeValues[1];
		}
		if (val) this.previouslySelectedFilterId = val.id;
	},
	data() {
		return {
			selectedLayerValue: this.initialLayerValue,
			selectedPropValue: this.initialPropValue,
			activeMin: 1,
			activeMax: -1,
			activeMinFake: 1,
			activeMaxFake: -1,
			startDateFake: moment().subtract(7, "days").format("YYYY-MM-DD"),
			endDateFake: moment().format("YYYY-MM-DD"),
			startDate: moment().subtract(7, "days").format("YYYY-MM-DD"),
			endDate: moment().format("YYYY-MM-DD"),
			previouslySelectedFilterId: null as string | null
		};
	},
	methods: {
		changeStartDateFake(value: number | any) {
			if (value?.target?.value == null) return;
			value = Number(moment(value?.target?.value).unix());
			if (value == null) return;
			if (value > Number(moment(this.endDateFake).unix())) {
				this.startDateFake = this.endDateFake;
				return;
			}
			if (!moment(value).isValid()) return;
			if (
				this.existingActiveFilterInclHidden &&
				this.existingActiveFilterInclHidden.hiddenRangeValues
			) {
				this.filtersStore.SSA_FILTER_SET_HIDDEN_THRESHOLD_VAL(
					this.existingActiveFilterInclHidden,
					0,
					value
				);
			}
		},
		changeEndDateFake(value: number | any) {
			if (value?.target?.value == null) return;
			value = Number(moment(value?.target?.value).unix());
			if (value == null) return;
			if (value < Number(moment(this.startDateFake).unix())) {
				this.endDateFake = this.startDateFake;
			}
			if (!moment(value).isValid()) return;
			if (
				this.existingActiveFilterInclHidden &&
				this.existingActiveFilterInclHidden.hiddenRangeValues
			) {
				this.filtersStore.SSA_FILTER_SET_HIDDEN_THRESHOLD_VAL(
					this.existingActiveFilterInclHidden,
					1,
					value
				);
			}
		},
		changeStartDate(value: string | any) {
			if (value?.target?.value == null) return;
			value = Number(moment(value?.target?.value).unix());
			if (value == null) return;
			if (value > Number(moment(this.endDate).unix())) {
				this.startDate = this.endDate;
				return;
			}
			if (!moment(value).isValid()) return;
			const filter = this.existingActiveFilter;
			if (filter?.ThresholdRange?.[1]) {
				filter.ThresholdRange[0] = value;
				this.updateSelectedFilter(filter);
			}
			return;
		},
		changeEndDate(value: string | any) {
			if (value?.target?.value == null) return;
			value = Number(moment(value?.target?.value).unix());
			if (value == null) return;
			if (value < Number(moment(this.startDate).unix())) {
				this.endDate = this.startDate;
			}
			if (!moment(value).isValid()) return;
			const filter = this.existingActiveFilter;
			if (filter?.ThresholdRange?.[1] && value > filter.ThresholdRange[0]) {
				filter.ThresholdRange[1] = value;
				this.updateSelectedFilter(filter);
			}
			return;
		},
		sortOnBlockSortOrder(a: any, b: any) {
			const indexA = BlockSortOrder.indexOf(a.text);
			const indexB = BlockSortOrder.indexOf(b.text);
			return indexA - indexB;
		},
		changeMin(value: number | any) {
			if (typeof value !== "number") {
				value = Number(value?.target?.value);
			}
			const filter = this.existingActiveFilter;
			if (filter?.ThresholdRange?.[1]) {
				filter.ThresholdRange[0] = value;
				this.updateSelectedFilter(filter);
			}
			return;
		},
		changeMax(value: number | any) {
			if (typeof value !== "number") {
				value = Number(value?.target?.value);
			}
			const filter = this.existingActiveFilter;
			if (filter?.ThresholdRange?.[1] && value > filter.ThresholdRange[0]) {
				filter.ThresholdRange[1] = value;
				this.updateSelectedFilter(filter);
			}
			return;
		},
		resetRange() {
			if (this.activeSelectedProp?.isTimeStamp) {
				this.resetDateRange();
				return;
			}
			this.activeMin = this.activeLayerState.fields[this.selectedPropValue]?.min;
			this.activeMax = this.activeLayerState.fields[this.selectedPropValue]?.max;
			const resetingFilter = JSON.parse(JSON.stringify(this.existingActiveFilterInclHidden)); // to avoid the anti-pattern of updating state outside of the store on line 214
			if (!resetingFilter) return;
			resetingFilter.ThresholdRange = [this.activeMin, this.activeMax];
			this.updateSelectedFilter(resetingFilter);
			return;
		},
		changeMinFake(val: number | any) {
			if (typeof val !== "number") {
				val = Number(val?.target?.value);
			}
			if (
				this.existingActiveFilterInclHidden &&
				this.existingActiveFilterInclHidden.hiddenRangeValues
			) {
				this.filtersStore.SSA_FILTER_SET_HIDDEN_THRESHOLD_VAL(
					this.existingActiveFilterInclHidden,
					0,
					val
				);
			}
		},
		changeMaxFake(val: number | any) {
			if (typeof val !== "number") {
				val = Number(val?.target?.value);
			}
			if (
				this.existingActiveFilterInclHidden &&
				this.existingActiveFilterInclHidden.hiddenRangeValues
			) {
				this.filtersStore.SSA_FILTER_SET_HIDDEN_THRESHOLD_VAL(
					this.existingActiveFilterInclHidden,
					1,
					val
				);
			}
		},
		resetDateRange() {
			this.startDate = moment
				.unix(this.activeLayerState.fields[this.selectedPropValueFormatted]?.min)
				.format("YYYY-MM-DD");
			this.endDate = moment
				.unix(this.activeLayerState.fields[this.selectedPropValueFormatted]?.max)
				.format("YYYY-MM-DD");

			const resetingFilter = JSON.parse(JSON.stringify(this.existingActiveFilterInclHidden)); // to avoid the anti-pattern of updating state outside of the store on line 214
			if (!resetingFilter) return;
			resetingFilter.ThresholdRange = [
				moment(this.startDate).unix(),
				moment(this.endDate).unix()
			];
			this.updateSelectedFilter(resetingFilter);
			return;
		},
		resetDateRangeFake() {
			this.startDateFake = moment
				.unix(this.activeLayerState.fields[this.selectedPropValueFormatted]?.min)
				.format("YYYY-MM-DD");
			this.endDateFake = moment
				.unix(this.activeLayerState.fields[this.selectedPropValueFormatted]?.max)
				.format("YYYY-MM-DD");

			if (this.existingActiveFilterInclHidden) {
				this.filtersStore.SSA_FILTER_SET_HIDDEN_THRESHOLD_VAL(
					this.existingActiveFilterInclHidden,
					0,
					moment(this.startDateFake).unix()
				);
				this.filtersStore.SSA_FILTER_SET_HIDDEN_THRESHOLD_VAL(
					this.existingActiveFilterInclHidden,
					1,
					moment(this.endDateFake).unix()
				);
			}
			return;
		},
		resetRangeFake() {
			if (this.activeSelectedProp?.isTimeStamp) {
				this.resetDateRangeFake();
				return;
			}
			this.activeMinFake = this.activeLayerState.fields[this.selectedPropValue]?.min;
			this.activeMaxFake = this.activeLayerState.fields[this.selectedPropValue]?.max;
			if (this.existingActiveFilterInclHidden) {
				this.filtersStore.SSA_FILTER_SET_HIDDEN_THRESHOLD_VAL(
					this.existingActiveFilterInclHidden,
					0,
					this.activeLayerState.fields[this.selectedPropValue]?.min
				);
				this.filtersStore.SSA_FILTER_SET_HIDDEN_THRESHOLD_VAL(
					this.existingActiveFilterInclHidden,
					1,
					this.activeLayerState.fields[this.selectedPropValue]?.max
				);
			}
			return;
		},
		updateSelectedFilter(filter: any) {
			const layerName = this.selectedLayerValue;
			const viewId = this.activeView;
			this.pvwStore.PVW_FILTER_THRESHOLD_UPDATE({ layerName, viewId, filter });
		}
	},

	computed: {
		selectedPropValueFormatted(): any {
			if (this.activeSelectedProp?.isTimeStamp) {
				return this.activeSelectedProp.value + "__DATETIME__";
			}
			return this.activeSelectedProp?.value;
		},
		activeSelectedProp() {
			return this.propItems.find(
				(prop) => prop.value.split("__DATETIME__")[0] == this.selectedPropValue
			);
		},
		activeLayerState() {
			return (this.activeStore as any).SSA_ACTIVE_LAYER_STATE;
		},
		activeLayersForDataset() {
			return this.displayStates[this.activeView][this.activeDataset.id];
		},
		existingFilters(): ParaviewFilter[] | undefined {
			return this.activeLayerState?.filters;
		},
		existingActiveFilter(): ParaviewFilter | undefined {
			return (
				this.existingFilters &&
				this.existingFilters.find(
					(filter: ParaviewFilter) =>
						filter?.Scalars?.[1] == this.selectedPropValueFormatted
				)
			);
		},
		existingActiveFilterInclHidden(): ParaviewFilterPlus | undefined {
			return this.filtersStore.SSA_ALL_ACTIVE_FILTERS.find(
				(filter: ParaviewFilterPlus) =>
					filter?.Scalars?.[1] == this.selectedPropValueFormatted &&
					filter.layerName == this.selectedLayerValue
			);
		},
		layerItems(): SelectInputItems[] {
			if (this.datasets[this.activeView]) {
				const layers = this.datasets[this.activeView].paraviewLayersInfoSplit as {
					[layerName: string]: LayerConfig;
				};

				const selectedLayersList = Object.keys(layers).filter((layer) => {
					// I've limited this to excluding Blastdog Drilled Survey as filters work fine for normal drillhole survey files
					if (
						layers[layer].layerConfigType == "plannedSurvey" ||
						layers[layer].displayPaneLabel === "Drilled Survey"
					)
						return false;

					return this.activeLayersForDataset.find((item: any) => {
						return item.selected && item.layerName == layer;
					});
				});

				const res = selectedLayersList
					.map((layerName) => {
						return {
							text: layers[layerName].displayPaneLabel,
							value: layerName
						};
					})
					.sort(this.sortOnBlockSortOrder);
				return res;
			} else {
				// add error message (no files found, please report)
				return [];
			}
		},
		propItems(): SelectInputItems[] {
			if (this.datasets[this.activeView] && this.activeLayerState) {
				const columns = this.datasets[this.activeView]
					.datasetColumnMapping as IParaviewColumnMapping[];

				const selectedLayerValueSplit = this.selectedLayerValue.split("/");
				let layerIdentifier = selectedLayerValueSplit.pop();

				if (!layerIdentifier) return [];
				if (this.datasets[this.activeView].dataType === ("Drillhole" as DatasetType))
					layerIdentifier = LayerNameToColumnLayerIdentifier(
						selectedLayerValueSplit,
						layerIdentifier
					);
				const selectInputItems = [] as SelectInputItems[];
				for (const column of columns) {
					if (
						column.layerIdentifier == layerIdentifier &&
						(column.isCategorical !== "true" || column.isTimeStamp) &&
						this.activeLayerState.fields[column.column]
					) {
						selectInputItems.push({
							text: column.label,
							isTimeStamp: column.isTimeStamp,
							value: column.column
						});
					}
				}
				return selectInputItems;
			} else {
				// add error message (no props found, please report)
				return [];
			}
		}
	},
	watch: {
		selectedLayerValue(val: any) {
			if (val && val != this.activeStore.SSA_ACTIVE_LAYER) this.activateLayer(val, true);
		},
		"activeLayerState.filters": {
			handler() {
				if (
					this.activeLayerState &&
					this.activeLayerState.filters &&
					this.activeLayerState.filters.length > 0 &&
					this.selectedPropValueFormatted
				) {
					const currentFilter = this.activeLayerState.filters.find(
						(filter: ParaviewFilterPlus) =>
							filter.Scalars[1].split("__DATETIME__")[0] == this.selectedPropValue
					);
					if (currentFilter) {
						this.startDate = moment
							.unix(currentFilter.ThresholdRange[0])
							.format("YYYY-MM-DD");
						this.endDate = moment
							.unix(currentFilter.ThresholdRange[1])
							.format("YYYY-MM-DD");
						this.activeMin = currentFilter.ThresholdRange[0];
						this.activeMax = currentFilter.ThresholdRange[1];
						this.previouslySelectedFilterId = currentFilter.id;
					}
				}
			},
			deep: true,
			immediate: true
		},
		propItems(newVal: any, oldVal: any): void {
			if (JSON.stringify(oldVal) !== JSON.stringify(newVal)) this.selectedPropValue = "";
		},
		selectedPropValue(newVal: any) {
			if (newVal !== "") {
				if (
					this.existingFilters &&
					this.existingFilters.length > 0 &&
					this.existingActiveFilter
				) {
					if (this.activeSelectedProp?.isTimeStamp) {
						this.startDate = moment
							.unix(this.existingActiveFilter.ThresholdRange[0])
							.format("YYYY-MM-DD");
						this.endDate = moment
							.unix(this.existingActiveFilter.ThresholdRange[1])
							.format("YYYY-MM-DD");
					} else if (
						this.existingActiveFilter.ThresholdRange[0] != this.activeMin ||
						this.existingActiveFilter.ThresholdRange[1] != this.activeMax
					) {
						this.activeMin = this.existingActiveFilter.ThresholdRange[0];
						this.activeMax = this.existingActiveFilter.ThresholdRange[1];
					}
					// return doing nothing if filter exists
					return;
				} else {
					// if we were editing a filter previously, remove it and flag it so we can persist the order for our replacement filter
					if (this.previouslySelectedFilterId !== null) {
						const previouslySelectedFilter: ParaviewFilterPlus | undefined =
							this.filtersStore.SSA_ALL_ACTIVE_FILTERS.find(
								(filter: ParaviewFilterPlus) => {
									return filter.id == this.previouslySelectedFilterId;
								}
							);

						if (previouslySelectedFilter) {
							this.filtersStore.SSA_REPLACE_FILTER_LAYER(previouslySelectedFilter);
							this.pvwStore.PVW_FILTER_THRESHOLD_REMOVE({
								layerName: previouslySelectedFilter.layerName,
								viewId: this.activeStore.view,
								filterId: previouslySelectedFilter.id
							});
						}
					}

					if (this.activeSelectedProp?.isTimeStamp) {
						const payload = {
							layerName: this.selectedLayerValue,
							viewId: this.activeView,
							field: newVal + "__DATETIME__",
							location:
								this.activeStore.SSA_ACTIVE_LAYER_CONFIG.options.field_location,
							dataRange: [
								this.activeLayerState.fields[newVal + "__DATETIME__"]?.min,
								this.activeLayerState.fields[newVal + "__DATETIME__"]?.max
							]
						};

						this.pvwStore.PVW_FILTER_THRESHOLD_ADD(payload);
					} else {
						// otherwise, create it with max range values
						const payload = {
							layerName: this.selectedLayerValue,
							viewId: this.activeView,
							field: newVal,
							location:
								this.activeStore.SSA_ACTIVE_LAYER_CONFIG.options.field_location,
							dataRange: [
								this.activeLayerState.fields[newVal]?.min,
								this.activeLayerState.fields[newVal]?.max
							]
						};

						this.pvwStore.PVW_FILTER_THRESHOLD_ADD(payload);
					}
				}
			}
		}
	}
});
